import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/rpc";
import { useAccount } from "@/lib/session";
import { myProgress } from "@/lib/progress.functions";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import * as React from "react";
import { Link, useRouterState, useNavigate, type LinkProps } from "@/lib/router";
import {
  Settings,
  LogOut,
  Search,
  House,
  GraduationCap,
  Gamepad2,
  Star,
  LayoutGrid,
} from "lucide-react";
import { IconTile, Mascot, ProgressBar, type Tone } from "./Playful";
import { LanguageSwitcher, useT } from "@/lib/i18n";
import { NowPlaying } from "./audio";
import { SiteFooter } from "./SiteFooter";
import { useAccess } from "@/lib/use-access";
import { cn } from "@/lib/utils";

const nav = [
  { to: "/", label: "होम", en: "Home", emoji: "🏠", tone: "purple" },
  { to: "/lesson", label: "मेरे पाठ", en: "My lessons", emoji: "📚", tone: "sky" },
  { to: "/grammar", label: "व्याकरण", en: "Grammar", emoji: "🧩", tone: "pink" },
  { to: "/vocabulary", label: "शब्द सीखें", en: "Words", emoji: "🔤", tone: "mint" },
  { to: "/sentences", label: "वाक्य अभ्यास", en: "Sentences", emoji: "💬", tone: "lagoon" },
  { to: "/stories", label: "कथा-वाटिका", en: "Stories", emoji: "🌳", tone: "peach" },
  { to: "/tirthankar", label: "चौबीस तीर्थंकर", en: "24 Tirthankaras", emoji: "🪷", tone: "lavender" },
  { to: "/games", label: "खेल", en: "Games", emoji: "🎮", tone: "peach" },
  { to: "/practice", label: "सुनो और सीखो", en: "Listen & learn", emoji: "🎧", tone: "sky" },
  { to: "/leaderboard", label: "लीडरबोर्ड", en: "Leaderboard", emoji: "🏆", tone: "mango" },
] as const satisfies readonly { to: string; label: string; en: string; emoji: string; tone: Tone }[];

const more = [
  { to: "/reader", label: "पाठ-वाचन", en: "Reader", emoji: "📖", tone: "lavender" },
  { to: "/contact", label: "संपर्क", en: "Contact", emoji: "✉️", tone: "pink" },
  { to: "/dictionary", label: "शब्दकोश", en: "Dictionary", emoji: "🔍", tone: "sky" },
  { to: "/curriculum", label: "पाठ्यक्रम", en: "Curriculum", emoji: "🗂️", tone: "peach" },
  { to: "/about", label: "परिचय", en: "About", emoji: "🌼", tone: "pink" },
  { to: "/settings", label: "सेटिंग्स", en: "Settings", emoji: "⚙️", tone: "lavender" },
] as const satisfies readonly { to: string; label: string; en: string; emoji: string; tone: Tone }[];

const bottom = [
  { to: "/", label: "होम", en: "Home", icon: House },
  { to: "/lesson", label: "पाठ", en: "Lessons", icon: GraduationCap },
  { to: "/games", label: "खेल", en: "Games", icon: Gamepad2 },
  { to: "/leaderboard", label: "लीडरबोर्ड", en: "Leaderboard", icon: Star },
] as const;

/** Real XP/level for signed-in learners (shares the dashboard query cache). */
export function useLearnerStats() {
  const account = useAccount();
  const fetch = useServerFn(myProgress);
  const enabled = !!account.profile && !account.isAdmin && !account.profile.must_change_password;
  const q = useQuery({
    queryKey: ["my-progress", account.user?.id],
    queryFn: () => fetch(),
    enabled,
    retry: false,
  });
  return enabled ? q.data : undefined;
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const path = useRouterState({ select: (state) => state.location.pathname });
  const account = useAccount();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  async function signOut() { await queryClient.cancelQueries(); queryClient.clear(); await supabase.auth.signOut(); await navigate({to:"/auth", replace:true}); }
  const stats = useLearnerStats();
  const [open, setOpen] = React.useState(false);
  const t = useT();

  if (path.startsWith("/lesson/")) return <>{children}</>;
  if (path === "/" && !account.loading && !account.user) return <>{children}</>;

  const name = account.profile?.display_name ?? "";
  const accountLink = account.isAdmin ? "/admin" : "/dashboard";

  return (
    <div className="min-h-screen lg:pl-[17.5rem]">
      <a
        href="#main"
        className="sr-only z-50 rounded-full bg-primary px-4 py-2 text-primary-foreground focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      >
        {t("मुख्य सामग्री पर जाएँ", "Skip to main content")}
      </a>

      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[17.5rem] p-4 lg:block">
        <div className="bubble flex h-full flex-col gap-4 overflow-y-auto p-4">
          <Brand />
          <nav aria-label={t("मुख्य नेविगेशन", "Main navigation")} className="flex flex-col gap-1">
            {nav.map((item) => (
              <NavItem key={item.to} to={item.to} label={t(item.label, item.en)} emoji={item.emoji} tone={item.tone} />
            ))}
          </nav>
          <div className="mt-auto space-y-3">
            {stats ? (
              <Link to="/leaderboard" search={{ tab: "mine" }} className="block rounded-3xl bg-dreamy p-4 transition hover:-translate-y-0.5">
                <p className="deva text-xs font-semibold text-accent-foreground">{t("स्तर", "Level")} {stats.level.level}</p>
                <p className="deva font-display text-lg font-bold">{stats.level.name}</p>
                <ProgressBar
                  className="mt-2 bg-white/60"
                  value={stats.total - stats.level.min}
                  max={stats.level.next ? stats.level.next.min - stats.level.min : 1}
                  label={t("अगले स्तर तक प्रगति", "Progress to next level")}
                />
                <p className="mt-2 text-xs font-semibold text-accent-foreground">
                  {stats.level.next ? t(`${stats.level.toNext} XP और`, `${stats.level.toNext} XP to go`) : t("सर्वोच्च स्तर", "Highest level")}
                </p>
              </Link>
            ) : !account.user ? (
              <div className="relative overflow-hidden rounded-3xl bg-dreamy p-4">
                <Mascot mood="wave" className="absolute -right-3 -bottom-4 size-20" />
                <p className="deva max-w-[9rem] text-sm font-bold">{t("लॉगिन करो और अपनी प्रगति सहेजो!", "Log in to save your progress!")}</p>
                <Button asChild size="sm" className="mt-3">
                  <Link to="/auth">{t("लॉगिन", "Log in")}</Link>
                </Button>
              </div>
            ) : null}
            <div className="flex flex-wrap gap-x-4 gap-y-1 px-2 text-xs font-semibold text-muted-foreground">
              {more.map((m) => (
                <Link key={m.to} to={m.to} className="hover:text-primary">
                  {t(m.label, m.en)}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </aside>

      {/* Slim header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center gap-2 px-4 py-3 sm:px-6">
          <div className="lg:hidden">
            <Brand compact />
          </div>
          <Link
            to="/dictionary"
            className="hidden h-11 flex-1 items-center gap-3 rounded-full bg-card px-5 text-sm text-muted-foreground shadow-[var(--shadow-card)] transition hover:text-primary md:flex md:max-w-sm"
          >
            <Search className="size-4" /> {t("कोई शब्द खोजें…", "Search a word…")}
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <LanguageSwitcher className="hidden sm:inline-flex" />
            <NowPlaying />
            <UpgradePill />
            {stats && (
              <>
                <Link
                  to="/leaderboard"
                  search={{ tab: "mine" }}
                  className="hidden h-11 items-center gap-1.5 rounded-full bg-card px-4 text-sm font-extrabold shadow-[var(--shadow-card)] sm:inline-flex"
                  aria-label={t(`निरंतरता ${stats.streak.current} दिन`, `Streak ${stats.streak.current} days`)}
                >
                  🔥 {stats.streak.current}
                </Link>
                <Link
                  to="/leaderboard"
                  search={{ tab: "mine" }}
                  className="inline-flex h-11 items-center gap-1.5 rounded-full bg-card px-4 text-sm font-extrabold shadow-[var(--shadow-card)]"
                  aria-label={t(`कुल ${stats.total} XP`, `Total ${stats.total} XP`)}
                >
                  ⭐ {stats.total}
                </Link>
              </>
            )}
            <Link
              to="/dictionary"
              className="grid size-11 place-items-center rounded-full bg-card shadow-[var(--shadow-card)] hover:text-primary md:hidden"
              aria-label={t("शब्द खोजें", "Search words")}
            >
              <Search className="size-5" />
            </Link>
            <Link
              to="/settings"
              className="hidden size-11 place-items-center rounded-full bg-card shadow-[var(--shadow-card)] hover:text-primary sm:grid"
              aria-label={t("सेटिंग्स", "Settings")}
            >
              <Settings className="size-5" />
            </Link>
            {account.user ? (
              <>
                <Link
                  to={accountLink}
                  className="grid size-11 place-items-center rounded-full bg-candy font-display text-lg font-bold text-white shadow-soft"
                  aria-label={account.isAdmin ? t("व्यवस्थापन", "Administration") : t("मेरी प्रगति", "My progress")}
                  title={name}
                >
                  {name ? Array.from(name)[0] : "🙂"}
                </Link>
                <Button onClick={signOut} variant="ghost" size="icon" aria-label={t("साइन आउट", "Sign out")} title={t("साइन आउट", "Sign out")} className="hidden sm:inline-flex">
                  <LogOut />
                </Button>
              </>
            ) : (
              <Button asChild>
                <Link to="/auth">{t("लॉगिन", "Log in")}</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      <main id="main" className="mx-auto w-full max-w-6xl px-4 pb-12 pt-2 sm:px-6">
        {children}
      </main>

      <SiteFooter className="pb-28 lg:pb-4" />

      {/* Mobile bottom navigation */}
      <nav
        aria-label={t("मोबाइल नेविगेशन", "Mobile navigation")}
        className="fixed inset-x-3 bottom-3 z-40 rounded-[1.75rem] bg-card/95 px-2 pb-[max(0.25rem,env(safe-area-inset-bottom))] pt-1 shadow-[0_18px_40px_-14px_rgb(59_47_122/0.35)] backdrop-blur lg:hidden"
      >
        <ul className="grid grid-cols-5">
          {bottom.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                activeOptions={{ exact: item.to === "/" }}
                className="group flex min-h-14 flex-col items-center justify-center gap-0.5 rounded-2xl text-[0.7rem] font-bold text-muted-foreground aria-[current=page]:text-primary"
              >
                <item.icon className="size-6 transition group-aria-[current=page]:scale-110 group-aria-[current=page]:fill-primary/20" />
                <span className="deva">{t(item.label, item.en)}</span>
              </Link>
            </li>
          ))}
          <li>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="flex min-h-14 w-full flex-col items-center justify-center gap-0.5 rounded-2xl text-[0.7rem] font-bold text-muted-foreground"
                >
                  <LayoutGrid className="size-6" />
                  <span className="deva">{t("और", "More")}</span>
                </button>
              </SheetTrigger>
              <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-[2rem] border-0 bg-background p-5 pb-8">
                <SheetTitle className="deva font-display text-xl">{t("कहाँ चलें? 🎈", "Where to? 🎈")}</SheetTitle>
                <LanguageSwitcher className="mt-3" />
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {[...nav, ...more].map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setOpen(false)}
                      activeOptions={{ exact: item.to === "/" }}
                      className="bubble flex flex-col items-center gap-2 p-3 text-center aria-[current=page]:ring-2 aria-[current=page]:ring-primary"
                    >
                      <IconTile tone={item.tone} size="sm">
                        {item.emoji}
                      </IconTile>
                      <span className="deva text-xs font-bold leading-tight">{t(item.label, item.en)}</span>
                    </Link>
                  ))}
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  {account.user ? (
                    <>
                      <Button asChild className="flex-1">
                        <Link to={accountLink} onClick={() => setOpen(false)}>
                          {account.isAdmin ? t("व्यवस्थापन", "Administration") : t("मेरा खाता", "My account")}
                        </Link>
                      </Button>
                      <Button variant="outline" onClick={() => { setOpen(false); void signOut(); }}>
                        <LogOut /> {t("साइन आउट", "Sign out")}
                      </Button>
                    </>
                  ) : (
                    <Button asChild className="flex-1">
                      <Link to="/auth" onClick={() => setOpen(false)}>{t("लॉगिन", "Log in")}</Link>
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </li>
        </ul>
      </nav>
    </div>
  );
}

function Brand({ compact = false }: { compact?: boolean }) {
  const t = useT();
  if (!compact) {
    return (
      <Link to="/" className="block rounded-3xl px-2 pt-1 transition hover:scale-[1.02]" aria-label={t("प्राकृत सरिता — मुखपृष्ठ", "Prakrit Sarita — home")}>
        <img src="/prakrit-sarita-logo.webp" alt="प्राकृत सरिता · Prakrit Sarita" width={640} height={640} className="mx-auto h-auto w-44" />
      </Link>
    );
  }
  return (
    <Link to="/" className="flex items-center gap-2">
      <img src="/prakrit-sarita-icon.png" alt="" width={40} height={40} className="size-10" />
      <span className="leading-tight">
        <span className="deva block font-display text-base font-extrabold text-ink sm:text-lg">प्राकृत सरिता</span>
        <span className="sr-only">{t("मुखपृष्ठ", "Home")}</span>
      </span>
    </Link>
  );
}

function NavItem({ to, label, emoji, tone }: { to: NonNullable<LinkProps["to"]>; label: string; emoji: string; tone: Tone }) {
  return (
    <Link
      to={to}
      activeOptions={{ exact: to === "/" }}
      className="group flex min-h-12 items-center gap-3 rounded-2xl px-2 py-1.5 text-[0.95rem] font-bold text-foreground/80 transition hover:bg-accent hover:text-accent-foreground aria-[current=page]:bg-candy aria-[current=page]:text-white aria-[current=page]:shadow-soft"
    >
      <IconTile tone={tone} size="sm" className="size-9 rounded-xl text-lg group-aria-[current=page]:from-white/90 group-aria-[current=page]:to-white/60">
        {emoji}
      </IconTile>
      <span className="deva">{label}</span>
    </Link>
  );
}

export function PageHeader({
  title,
  sub,
  emoji,
  tone = "purple",
}: {
  title: React.ReactNode;
  sub?: React.ReactNode | undefined;
  emoji?: string;
  tone?: Tone;
}) {
  const t = useT();
  return (
    <header className="mb-8 flex items-center gap-4">
      {emoji && (
        <IconTile tone={tone} size="lg" className="hidden sm:inline-grid">
          {emoji}
        </IconTile>
      )}
      <div className="min-w-0">
        <p className="deva text-xs font-bold tracking-wide text-primary">{t("प्राकृत सरिता", "Prakrit Sarita")}</p>
        <h1 className="deva mt-1 text-3xl font-extrabold md:text-4xl">{title}</h1>
        {sub && <p className="deva mt-1 max-w-3xl text-base text-muted-foreground">{sub}</p>}
        <div className="lotus-rule mt-3" />
      </div>
    </header>
  );
}

/** A quiet header link for signed-in learners without a membership — never a popup. */
function UpgradePill() {
  const access = useAccess();
  if (!access.signedIn || access.loading || access.full) return null;
  return (
    <Link
      to="/pricing"
      className="hidden min-h-11 items-center gap-1 rounded-full bg-[#fff4d6] px-4 text-sm font-extrabold text-[#865600] shadow-[var(--shadow-card)] transition hover:-translate-y-0.5 sm:inline-flex"
    >
      ✨ Upgrade
    </Link>
  );
}
